// @ts-strict-ignore
import * as React from 'react';
import { AppComponentProps } from '../../typescriptHelpers';
import Text from 'antd/lib/typography/Text';
import {
    displayTextForDayOfTheWeek,
    displayTextForReleaseScheduleCadenceStyle,
    tooltipDisplayLifecycleEventName,
} from '../../helpers/displayHelpers';
import { useForm, useWatch } from 'antd/lib/form/Form';
import FormList from 'antd/lib/form/FormList';
import FormItem from 'antd/lib/form/FormItem';
import Card from 'antd/lib/card/Card';
import Select from 'antd/lib/select';
import {
    AllDaysOfTheWeek,
    AllReleaseScheduleCadenceStyles,
    AppStoreType,
    DayOfTheWeek,
    LifecycleFreezeSet,
    CadenceTargetDateSet,
    ReleaseScheduleCadenceStyle,
    LifecycleEventType,
} from '../../types';
import Divider from 'antd/lib/divider';
import Input from 'antd/lib/input';
import TimePicker from 'antd/lib/time-picker';
import Space from 'antd/lib/space';
import Layout, { Footer } from 'antd/lib/layout/layout';
import { antGrey, runwayColors } from '../styles/globalStyles';
import { CloseOutlined, InfoCircleOutlined, PlusOutlined } from '@ant-design/icons';
import styles from '../styles/stepStyles';
import { AppSettingsUpdatable } from '../../actions/updatable';
import { ReleaseScheduleCadenceSettingsRequest, tryUpdateAppSettings } from '../../actions/appSettings';
import { useDispatch, useSelector } from 'react-redux';
import { dayjs } from '@/extendedDayjs';
import Tooltip from 'antd/lib/tooltip';
import PermissionedActionButton from '../HOCs/PermissionedActionButton';
import { RunwayUserAction } from '../../helpers/aclHelpers';
import { globalMessage } from '../../utils/globalMessage';
import { selectAppPlatform, selectAppStoreType } from '../../selectors/selectors';
import { RootState } from '../../reducers/reducers';
import { ExtendedForm } from '@/utils/ExtendedForm';
import { useExtendedForm } from '@/utils/hooks/useExtendedForm';
import { useApp, useAppSettings, useError, useIsLoading, useUpdated } from '@/utils/hooks/helpers';
import Button from 'antd/lib/button';
import { getIsOTAPlatform, sortLifecycleFreezeSets, weekdayNumberFromDayOfWeek } from '@/helpers';
import { Checkbox, DatePicker } from 'antd/lib';

type ReleaseScheduleFormValues = {
    scheduleCadence: ReleaseScheduleCadenceStyle;
    cadenceTimezone: string | null;
    skipNextPossibleSubmit: boolean;
    skipNextPossibleRelease: boolean;
    updateExistingReleaseTargetDates: boolean;
    cadenceTargetSets: Array<{
        kickoffDayOfWeek: string | null;
        kickoffTime: dayjs.Dayjs | null;
        submitDayOfWeek: string | null;
        submitTime: dayjs.Dayjs | null;
        releaseDayOfWeek: string | null;
        releaseTime: dayjs.Dayjs | null;
    }>;
    lifecycleFreezes: Array<{
        lifecycleFreezeSetRange: [dayjs.Dayjs | null, dayjs.Dayjs | null];
        lifecycleFreezeAffectedEvents: Array<LifecycleEventType>;
    }>;
};

const DEFAULT_LIFECYCLE_FREEZE_EVENTS = [
    LifecycleEventType.LifecycleEventTypeKickoff,
    LifecycleEventType.LifecycleEventTypeSubmit,
    LifecycleEventType.LifecycleEventTypeRelease,
];

const ReleaseScheduleSettings: React.FC<AppComponentProps> = (props: AppComponentProps) => {
    const appSettings = useAppSettings(props);
    const appStoreType = useSelector((state: RootState) => selectAppStoreType(state, props.appId));
    const appPlatform = useSelector((state: RootState) => selectAppPlatform(state, props.appId));
    const isAppleAppStore = appStoreType === AppStoreType.Apple;
    const isOTA = getIsOTAPlatform(appPlatform);

    const app = useApp(props);
    const scheduleSettings = appSettings.releaseScheduleCadenceSettings;
    const dispatch = useDispatch();
    const [form] = useForm();
    const [releaseScheduleFormState, releaseScheduleExtendedForm] = useExtendedForm<ReleaseScheduleFormValues>();

    const usesStaticReleaseBranchForNonHotfixes = app.appSettings.usesStaticReleaseBranchForNonHotfixes;

    const kickoffDescription =
        usesStaticReleaseBranchForNonHotfixes ?
            'promoting the code in your working branch to your release branch'
        :   'creating a release branch';

    const defaultCadenceTargetSet = {
        kickoffDayOfWeek: null,
        kickoffTime: null, // am UTC
        submitDayOfWeek: null,
        submitTime: null,
        releaseDayOfWeek: null,
        releaseTime: null,
    };

    const defaultLifecycleFreezeSet = {
        startDate: null,
        endDate: null,
        affectedEvents: DEFAULT_LIFECYCLE_FREEZE_EVENTS,
    };

    const lifecycleEventOptions = DEFAULT_LIFECYCLE_FREEZE_EVENTS.map((event) => ({
        value: event,
        label: tooltipDisplayLifecycleEventName(event),
    }));

    const formInitialValues = React.useMemo(() => {
        const lifecycleFreezes =
            appSettings.lifecycleFreezes?.map(({ startDate, endDate, affectedEvents }) => {
                const parsedStartDate = dayjs(startDate).local();
                const parsedEndDate = dayjs(endDate).local();
                return {
                    lifecycleFreezeSetRange: [parsedStartDate, parsedEndDate],
                    lifecycleFreezeAffectedEvents: affectedEvents,
                };
            }) || [];

        if (scheduleSettings) {
            const cadenceTargetSets = scheduleSettings.cadenceTargetDateSets;

            const initialValues = {
                scheduleCadence: scheduleSettings.scheduleCadence,
                cadenceTimezone: scheduleSettings.cadenceTimezone ?? null,
                skipNextPossibleSubmit: scheduleSettings.skipNextPossibleSubmit,
                skipNextPossibleRelease: scheduleSettings.skipNextPossibleRelease,
                updateExistingReleaseTargetDates: false,
                cadenceTargetSets:
                    cadenceTargetSets && cadenceTargetSets.length > 0 ?
                        cadenceTargetSets.map((dateSet) => {
                            return {
                                kickoffDayOfWeek: dateSet.kickoffDayOfWeek ? dateSet.kickoffDayOfWeek : null,
                                kickoffTime:
                                    dateSet.kickoffTimeHour != null ?
                                        dayjs(`${dateSet.kickoffTimeHour}:${dateSet.kickoffTimeMinute ?? 0}`, 'hh:mm')
                                    :   null,
                                submitDayOfWeek: dateSet.submitDayOfWeek ? dateSet.submitDayOfWeek : null,
                                submitTime:
                                    dateSet.submitTimeHour !== null ?
                                        dayjs(`${dateSet.submitTimeHour}:${dateSet.submitTimeMinute ?? 0}`, 'hh:mm')
                                    :   null,
                                releaseDayOfWeek: dateSet.releaseDayOfWeek ? dateSet.releaseDayOfWeek : null,
                                releaseTime:
                                    dateSet.releaseTimeHour !== null ?
                                        dayjs(`${dateSet.releaseTimeHour}:${dateSet.releaseTimeMinute ?? 0}`, 'hh:mm')
                                    :   null,
                            };
                        })
                    :   [{ ...defaultCadenceTargetSet }],
                lifecycleFreezes,
            };

            return initialValues;
        } else {
            const initialValues = {
                scheduleCadence: ReleaseScheduleCadenceStyle.Never,
                skipNextPossibleSubmit: false,
                skipNextPossibleRelease: false,
                updateExistingReleaseTargetDates: false,
                lifecycleFreezes,
                cadenceTargetSets: [{ ...defaultCadenceTargetSet }],
                cadenceTimezone: dayjs.tz.guess(),
            };

            return initialValues;
        }
    }, [scheduleSettings]);

    const handleAddSet = () => {
        const currentDateSets = form.getFieldValue('cadenceTargetSets');
        const newValue = [...currentDateSets, { ...defaultCadenceTargetSet }];
        form.setFieldValue('cadenceTargetSets', newValue);
    };

    const handleAddLifecycleFreezeSet = () => {
        const currentLifecycleFreezeSets = form.getFieldValue('lifecycleFreezes');
        const newValue = [...currentLifecycleFreezeSets, { ...defaultLifecycleFreezeSet }];
        form.setFieldValue('lifecycleFreezes', newValue);
    };

    const cadenceTZVal = useWatch('cadenceTimezone', { form: form, preserve: true });
    const isCadenceTimezoneDirty = React.useMemo(() => {
        const initialTimezone = formInitialValues.cadenceTimezone ?? null;
        const currentTimezone = cadenceTZVal ?? null;
        return initialTimezone !== currentTimezone;
    }, [cadenceTZVal, formInitialValues.cadenceTimezone]);

    const cadenceTypeVal: ReleaseScheduleCadenceStyle = useWatch('scheduleCadence', { form: form });
    const cadenceSetsVal: ReleaseScheduleCadenceStyle = useWatch('cadenceTargetSets', { form: form });
    if (!releaseScheduleExtendedForm.form) {
        releaseScheduleExtendedForm.form = form;
    }

    const skipNextPossibleSubmitVal: boolean = useWatch('skipNextPossibleSubmit', { form: form, preserve: true });
    const skipNextPossibleReleaseVal: boolean = useWatch('skipNextPossibleRelease', { form: form, preserve: true });
    const updateExistingReleaseTargetDatesVal: boolean = useWatch('updateExistingReleaseTargetDates', {
        form: form,
        preserve: true,
    });

    const isSkipNextPossibleSubmitDirty = React.useMemo(() => {
        const initialSkipNextPossibleSubmit = formInitialValues.skipNextPossibleSubmit;
        const currentSkipNextPossibleSubmit = skipNextPossibleSubmitVal;
        return initialSkipNextPossibleSubmit !== currentSkipNextPossibleSubmit;
    }, [skipNextPossibleSubmitVal, formInitialValues.skipNextPossibleSubmit]);

    const isSkipNextPossibleReleaseDirty = React.useMemo(() => {
        const initialSkipNextPossibleRelease = formInitialValues.skipNextPossibleRelease;
        const currentSkipNextPossibleRelease = skipNextPossibleReleaseVal;
        return initialSkipNextPossibleRelease !== currentSkipNextPossibleRelease;
    }, [skipNextPossibleReleaseVal, formInitialValues.skipNextPossibleRelease]);

    const isUpdateExistingReleaseTargetDatesDirty = React.useMemo(() => {
        const initialUpdateExistingReleaseTargetDates = formInitialValues.updateExistingReleaseTargetDates ?? false;
        const currentUpdateExistingReleaseTargetDates = updateExistingReleaseTargetDatesVal ?? false;
        return initialUpdateExistingReleaseTargetDates !== currentUpdateExistingReleaseTargetDates;
    }, [updateExistingReleaseTargetDatesVal, formInitialValues.updateExistingReleaseTargetDates]);

    const onNext = (values) => {
        form.validateFields().then(() => {
            const cadenceTargetDateSets: CadenceTargetDateSet[] =
                values.cadenceTargetSets ?
                    values.cadenceTargetSets.map((dateSet) => {
                        return {
                            kickoffDayOfWeek: dateSet.kickoffDayOfWeek,
                            kickoffTimeHour: dateSet.kickoffTime ? (dateSet.kickoffTime as dayjs.Dayjs).hour() : null,
                            kickoffTimeMinute:
                                dateSet.kickoffTime ? (dateSet.kickoffTime as dayjs.Dayjs).minute() : null,
                            submitDayOfWeek: dateSet.submitDayOfWeek,
                            submitTimeHour: dateSet.submitTime ? (dateSet.submitTime as dayjs.Dayjs).hour() : null,
                            submitTimeMinute: dateSet.submitTime ? (dateSet.submitTime as dayjs.Dayjs).minute() : null,
                            releaseDayOfWeek: dateSet.releaseDayOfWeek,
                            releaseTimeHour: dateSet.releaseTime ? (dateSet.releaseTime as dayjs.Dayjs).hour() : null,
                            releaseTimeMinute:
                                dateSet.releaseTime ? (dateSet.releaseTime as dayjs.Dayjs).minute() : null,
                        };
                    })
                :   [];
            const lifecycleFreezes: LifecycleFreezeSet[] = values.lifecycleFreezes.map((dateSet) => {
                const [start, end] = dateSet.lifecycleFreezeSetRange;
                return {
                    startDate: start.format('YYYY-MM-DD'),
                    endDate: end.format('YYYY-MM-DD'),
                    affectedEvents: dateSet.lifecycleFreezeAffectedEvents,
                };
            });

            const isCadenceNever = values.scheduleCadence === ReleaseScheduleCadenceStyle.Never;

            const request: ReleaseScheduleCadenceSettingsRequest = {
                orgId: props.orgId,
                appId: props.appId,
                scheduleCadence: values.scheduleCadence,
                cadenceTargetDateSets: cadenceTargetDateSets,
                lifecycleFreezes: sortLifecycleFreezeSets(lifecycleFreezes),
                skipNextPossibleSubmit: isCadenceNever ? false : skipNextPossibleSubmitVal,
                skipNextPossibleRelease: isCadenceNever ? false : skipNextPossibleReleaseVal,
                cadenceTimezone: cadenceTZVal,
                updateExistingReleaseTargetDates:
                    isCadenceNever ? false : (updateExistingReleaseTargetDatesVal ?? false),
            };

            dispatch(tryUpdateAppSettings(AppSettingsUpdatable.UpdateReleaseScheduleCadenceSettings, request));
        });
    };

    const updateCadenceIsLoading = useIsLoading(AppSettingsUpdatable.UpdateReleaseScheduleCadenceSettings);
    const updateCadenceError = useError(AppSettingsUpdatable.UpdateReleaseScheduleCadenceSettings);
    const updateCadenceSuccess = useUpdated(AppSettingsUpdatable.UpdateReleaseScheduleCadenceSettings);

    React.useEffect(() => {
        if (updateCadenceError) {
            globalMessage.error(updateCadenceError);
        }
    }, [updateCadenceError]);

    React.useEffect(() => {
        if (updateCadenceSuccess) {
            globalMessage.success('Release schedule settings updated!');
        }
    }, [updateCadenceSuccess]);

    React.useEffect(() => {
        if (
            cadenceTypeVal &&
            !(
                cadenceTypeVal === ReleaseScheduleCadenceStyle.Weekly ||
                cadenceTypeVal === ReleaseScheduleCadenceStyle.Never
            )
        ) {
            const currentSets = form.getFieldValue('cadenceTargetSets');
            const newVal = currentSets?.length > 0 ? [{ ...currentSets[0] }] : [{ ...defaultCadenceTargetSet }];
            form.setFieldValue('cadenceTargetSets', newVal);
        }
    }, [cadenceTypeVal]);

    const compactFormItemStyle: React.CSSProperties = {
        maxWidth: '30%',
        minWidth: 'fit-content',
        marginRight: 80,
    };

    const timeInputStyle: React.CSSProperties = {
        width: '100%',
    };

    const selectInputStyle: React.CSSProperties = {
        minWidth: 120,
    };

    const inputGroupStyle: React.CSSProperties = {
        marginTop: 16,
        width: '100%',
        alignItems: 'flex-start',
    };

    return (
        <Layout>
            <ExtendedForm
                initialValues={formInitialValues}
                extendedFormObject={releaseScheduleExtendedForm}
                onFinish={onNext}
                onFinishFailed={() =>
                    globalMessage.error('Cadence form is invalid; please address problems, then resubmit.')
                }
                layout="vertical"
                preserve
            >
                <FormItem
                    label="Release schedule cadence"
                    name="scheduleCadence"
                    rules={[{ required: true }]}
                    style={{ width: '40%', minWidth: 300, maxWidth: 400 }}
                >
                    <Select placeholder="Select">
                        {AllReleaseScheduleCadenceStyles.map((cadenceStyle, index) => {
                            return (
                                <Select.Option value={cadenceStyle} key={index}>
                                    {displayTextForReleaseScheduleCadenceStyle(cadenceStyle)}
                                </Select.Option>
                            );
                        })}
                    </Select>
                </FormItem>
                <FormItem label="Timezone" style={{ width: '40%', minWidth: 300, maxWidth: 400 }} shouldUpdate>
                    <div style={{ minWidth: 150 }}>
                        <Select
                            placeholder="Select"
                            showSearch
                            disabled={cadenceTypeVal === ReleaseScheduleCadenceStyle.Never}
                            allowClear
                            value={cadenceTZVal}
                            onSelect={(value) => {
                                form.setFieldValue('cadenceTimezone', value);
                            }}
                            onClear={() => {
                                form.setFieldValue('cadenceTimezone', null);
                            }}
                        >
                            {Intl.supportedValuesOf('timeZone').map((timezone, index) => {
                                return (
                                    <Select.Option value={timezone} key={index}>
                                        {timezone} ({dayjs().tz(timezone).format('z')})
                                    </Select.Option>
                                );
                            })}
                        </Select>
                    </div>
                </FormItem>
                <Divider />
                <Text>
                    <div style={{ paddingBottom: '8px' }}>Release event dates</div>
                </Text>
                <FormList name="cadenceTargetSets">
                    {(fields, { remove }) => {
                        return (
                            <>
                                {fields.map((fieldData, i) => {
                                    return (
                                        <Card
                                            title={
                                                cadenceSetsVal?.length > 1 ?
                                                    <div
                                                        style={{
                                                            display: 'flex',
                                                            flexDirection: 'row',
                                                            alignItems: 'center',
                                                            justifyContent: 'space-between',
                                                        }}
                                                    >
                                                        <Text
                                                            style={{
                                                                width: 24,
                                                                height: 24,
                                                                borderRadius: 12,
                                                                backgroundColor: runwayColors.primary,
                                                                color: 'white',
                                                                display: 'flex',
                                                                justifyContent: 'center',
                                                                alignItems: 'center',
                                                                fontWeight: 'normal',
                                                                flexShrink: 0,
                                                            }}
                                                        >
                                                            {i + 1}
                                                        </Text>
                                                        <div style={{ visibility: i > 0 ? 'visible' : 'hidden' }}>
                                                            <Button
                                                                icon={<CloseOutlined />}
                                                                type="text"
                                                                disabled={
                                                                    i === 0 ||
                                                                    cadenceTypeVal === ReleaseScheduleCadenceStyle.Never
                                                                }
                                                                onClick={() => remove(i)}
                                                            />
                                                        </div>
                                                    </div>
                                                :   null
                                            }
                                            key={`&dateSet=${i}`}
                                            style={{ marginBottom: '15px' }}
                                        >
                                            <Space>
                                                <Text type="secondary">
                                                    Set a target date and time to kick off your release.
                                                </Text>
                                                <Tooltip
                                                    title={`If you have the auto-kickoff automation enabled, Runway will kick off your release by ${kickoffDescription} within 5 minutes after the target date. You can turn individual automations on or off from App Settings.`}
                                                >
                                                    <InfoCircleOutlined style={{ color: antGrey.grey7 }} />
                                                </Tooltip>
                                            </Space>
                                            <Input.Group compact style={inputGroupStyle}>
                                                <FormItem
                                                    label="Kickoff day"
                                                    name={[fieldData.name, 'kickoffDayOfWeek']}
                                                    style={compactFormItemStyle}
                                                    shouldUpdate
                                                    validateTrigger="onFinish"
                                                    rules={[
                                                        {
                                                            validator: (_, value) => {
                                                                const configuredKickoffTime = form.getFieldValue([
                                                                    'cadenceTargetSets',
                                                                    i,
                                                                    'kickoffTime',
                                                                ]);
                                                                if (configuredKickoffTime && !value) {
                                                                    return Promise.reject(
                                                                        'Kickoff day is required if kickoff time is configured',
                                                                    );
                                                                } else if (cadenceSetsVal?.length > 1) {
                                                                    if (!value) {
                                                                        return Promise.reject(
                                                                            'Kickoff configuration is required to support multiple releases per week',
                                                                        );
                                                                    }

                                                                    if (i > 0) {
                                                                        const priorSetKickoffDay = form.getFieldValue([
                                                                            'cadenceTargetSets',
                                                                            i - 1,
                                                                            'kickoffDayOfWeek',
                                                                        ]);

                                                                        if (
                                                                            priorSetKickoffDay != undefined &&
                                                                            weekdayNumberFromDayOfWeek(
                                                                                priorSetKickoffDay,
                                                                            ) >= weekdayNumberFromDayOfWeek(value)
                                                                        ) {
                                                                            return Promise.reject(
                                                                                'Must be a later weekday than the kickoff day selected in the preceding date set',
                                                                            );
                                                                        }
                                                                    }
                                                                }

                                                                return Promise.resolve();
                                                            },
                                                        },
                                                    ]}
                                                >
                                                    <Select
                                                        placeholder="Select"
                                                        allowClear
                                                        disabled={cadenceTypeVal === ReleaseScheduleCadenceStyle.Never}
                                                        style={selectInputStyle}
                                                    >
                                                        {AllDaysOfTheWeek.map((dayOfTheWeek, index) => {
                                                            return (
                                                                <Select.Option value={dayOfTheWeek} key={index}>
                                                                    {displayTextForDayOfTheWeek(dayOfTheWeek)}
                                                                </Select.Option>
                                                            );
                                                        })}
                                                    </Select>
                                                </FormItem>
                                                <FormItem
                                                    label="Kickoff time"
                                                    name={[fieldData.name, 'kickoffTime']}
                                                    style={compactFormItemStyle}
                                                    shouldUpdate
                                                    validateTrigger="onFinish"
                                                    rules={[
                                                        {
                                                            validator: (_, value) => {
                                                                const configuredKickoffDay = form.getFieldValue([
                                                                    'cadenceTargetSets',
                                                                    i,
                                                                    'kickoffDayOfWeek',
                                                                ]);
                                                                if (configuredKickoffDay && !value) {
                                                                    return Promise.reject(
                                                                        'Kickoff time is required if kickoff day of the week is selected',
                                                                    );
                                                                } else if (!value && cadenceSetsVal?.length > 1) {
                                                                    return Promise.reject(
                                                                        'Kickoff configuration is required to support multiple releases per week',
                                                                    );
                                                                } else {
                                                                    return Promise.resolve();
                                                                }
                                                            },
                                                        },
                                                    ]}
                                                >
                                                    {makeReleaseScheduleTimePicker(
                                                        timeInputStyle,
                                                        cadenceTypeVal === ReleaseScheduleCadenceStyle.Never,
                                                    )}
                                                </FormItem>
                                            </Input.Group>
                                            {!isOTA && (
                                                <div>
                                                    <Space>
                                                        <Text type="secondary">
                                                            Set a target date and time to submit your app for review.
                                                        </Text>
                                                        <Tooltip
                                                            title={`If you have the auto-submit automation enabled, Runway will submit your selected build for review if all previous steps are "Ready", within 5 minutes after the target date. You can turn individual automations on or off from App Settings.`}
                                                        >
                                                            <InfoCircleOutlined style={{ color: antGrey.grey7 }} />
                                                        </Tooltip>
                                                    </Space>
                                                    <Input.Group compact style={inputGroupStyle}>
                                                        <FormItem
                                                            label="Submit day"
                                                            name={[fieldData.name, 'submitDayOfWeek']}
                                                            style={compactFormItemStyle}
                                                            validateTrigger="onFinish"
                                                            rules={[
                                                                {
                                                                    validator: (_, value) => {
                                                                        const configuredSubmitTime = form.getFieldValue(
                                                                            ['cadenceTargetSets', i, 'submitTime'],
                                                                        );
                                                                        if (configuredSubmitTime && !value) {
                                                                            return Promise.reject(
                                                                                'Submit day is required if submit time is configured.',
                                                                            );
                                                                        } else if (i > 0) {
                                                                            const firstSetSubmitDay =
                                                                                form.getFieldValue([
                                                                                    'cadenceTargetSets',
                                                                                    0,
                                                                                    'submitDayOfWeek',
                                                                                ]);

                                                                            if (firstSetSubmitDay && !value) {
                                                                                return Promise.reject(
                                                                                    'Targets must be consistently configured across sets (submit day configured in one or more preceding sets)',
                                                                                );
                                                                            } else if (!firstSetSubmitDay && value) {
                                                                                return Promise.reject(
                                                                                    'Targets must be consistently configured across sets (submit day missing in one or more preceding sets)',
                                                                                );
                                                                            }
                                                                        }

                                                                        return Promise.resolve();
                                                                    },
                                                                },
                                                            ]}
                                                        >
                                                            <Select
                                                                placeholder="Select"
                                                                allowClear
                                                                disabled={
                                                                    cadenceTypeVal === ReleaseScheduleCadenceStyle.Never
                                                                }
                                                                style={selectInputStyle}
                                                            >
                                                                {AllDaysOfTheWeek.map((dayOfTheWeek, index) => {
                                                                    return (
                                                                        <Select.Option value={dayOfTheWeek} key={index}>
                                                                            {displayTextForDayOfTheWeek(dayOfTheWeek)}
                                                                        </Select.Option>
                                                                    );
                                                                })}
                                                            </Select>
                                                        </FormItem>
                                                        <FormItem
                                                            label="Submit time"
                                                            name={[fieldData.name, 'submitTime']}
                                                            style={compactFormItemStyle}
                                                            shouldUpdate
                                                            validateTrigger="onFinish"
                                                            rules={[
                                                                {
                                                                    validator: (_, value) => {
                                                                        const configuredSubmitDay = form.getFieldValue([
                                                                            'cadenceTargetSets',
                                                                            i,
                                                                            'submitDayOfWeek',
                                                                        ]);
                                                                        if (configuredSubmitDay && !value) {
                                                                            return Promise.reject(
                                                                                'Submit time is required if submit day of the week is selected',
                                                                            );
                                                                        } else if (i > 0) {
                                                                            const firstSetSubmitTime =
                                                                                form.getFieldValue([
                                                                                    'cadenceTargetSets',
                                                                                    0,
                                                                                    'submitTime',
                                                                                ]);

                                                                            if (firstSetSubmitTime && !value) {
                                                                                return Promise.reject(
                                                                                    'Targets must be consistently configured across sets (submit time configured in one or more preceding sets)',
                                                                                );
                                                                            } else if (!firstSetSubmitTime && value) {
                                                                                return Promise.reject(
                                                                                    'Targets must be consistently configured across sets (submit time missing in one or more preceding sets)',
                                                                                );
                                                                            }
                                                                        }

                                                                        return Promise.resolve();
                                                                    },
                                                                },
                                                            ]}
                                                        >
                                                            {makeReleaseScheduleTimePicker(
                                                                timeInputStyle,
                                                                cadenceTypeVal === ReleaseScheduleCadenceStyle.Never,
                                                            )}
                                                        </FormItem>
                                                        {i === 0 &&
                                                            cadenceTypeVal !== ReleaseScheduleCadenceStyle.Never && (
                                                                <FormItem
                                                                    label={<span>&nbsp;</span>}
                                                                    colon={false}
                                                                    name="skipNextPossibleSubmit"
                                                                    style={compactFormItemStyle}
                                                                    shouldUpdate
                                                                    validateTrigger="onFinish"
                                                                >
                                                                    <Space size={6}>
                                                                        <Checkbox
                                                                            checked={form.getFieldValue(
                                                                                'skipNextPossibleSubmit',
                                                                            )}
                                                                            onChange={() => {
                                                                                const value =
                                                                                    form.getFieldValue(
                                                                                        'skipNextPossibleSubmit',
                                                                                    );
                                                                                form.setFieldValue(
                                                                                    'skipNextPossibleSubmit',
                                                                                    !value,
                                                                                );
                                                                            }}
                                                                        />
                                                                        <Text>Following week</Text>
                                                                        <Tooltip
                                                                            title={
                                                                                <div>
                                                                                    <p>
                                                                                        By default, Runway will schedule
                                                                                        submission for the next
                                                                                        available instance of the
                                                                                        selected target day in the
                                                                                        calendar (e.g. if Monday is
                                                                                        selected, Runway will schedule
                                                                                        for the first possible Monday
                                                                                        after kickoff).
                                                                                    </p>
                                                                                    Enable this option to instead have
                                                                                    Runway schedule submission for one
                                                                                    week later (e.g. if Monday is
                                                                                    selected, Runway will skip the first
                                                                                    Monday after kickoff and instead
                                                                                    schedule for the following Monday).
                                                                                </div>
                                                                            }
                                                                        >
                                                                            <InfoCircleOutlined
                                                                                style={{ color: antGrey.grey7 }}
                                                                            />
                                                                        </Tooltip>
                                                                    </Space>
                                                                </FormItem>
                                                            )}
                                                    </Input.Group>
                                                </div>
                                            )}
                                            {(isAppleAppStore || isOTA) && (
                                                <div>
                                                    <Space>
                                                        <Text type="secondary">
                                                            Set a target date and time to release your app update.
                                                        </Text>
                                                        <Tooltip
                                                            title={`If you have the auto-release automation enabled, Runway will release your approved build within 5 minutes after the target date. You can turn individual automations on or off from App Settings.`}
                                                        >
                                                            <InfoCircleOutlined style={{ color: antGrey.grey7 }} />
                                                        </Tooltip>
                                                    </Space>
                                                    <Input.Group compact style={inputGroupStyle}>
                                                        <FormItem
                                                            label="Release day"
                                                            name={[fieldData.name, 'releaseDayOfWeek']}
                                                            style={compactFormItemStyle}
                                                            validateTrigger="onFinish"
                                                            rules={[
                                                                {
                                                                    validator: (_, value) => {
                                                                        const configuredReleaseTime =
                                                                            form.getFieldValue([
                                                                                'cadenceTargetSets',
                                                                                i,
                                                                                'releaseTime',
                                                                            ]);
                                                                        if (configuredReleaseTime && !value) {
                                                                            return Promise.reject(
                                                                                'Release day is required if release time is configured.',
                                                                            );
                                                                        } else if (i > 0) {
                                                                            const firstSetReleaseDay =
                                                                                form.getFieldValue([
                                                                                    'cadenceTargetSets',
                                                                                    0,
                                                                                    'releaseDayOfWeek',
                                                                                ]);

                                                                            if (firstSetReleaseDay && !value) {
                                                                                return Promise.reject(
                                                                                    'Targets must be consistently configured across sets (release day configured in one or more preceding sets)',
                                                                                );
                                                                            } else if (!firstSetReleaseDay && value) {
                                                                                return Promise.reject(
                                                                                    'Targets must be consistently configured across sets (release day missing in one or more preceding sets)',
                                                                                );
                                                                            }
                                                                        }

                                                                        return Promise.resolve();
                                                                    },
                                                                },
                                                            ]}
                                                        >
                                                            <Select
                                                                placeholder="Select"
                                                                disabled={
                                                                    cadenceTypeVal === ReleaseScheduleCadenceStyle.Never
                                                                }
                                                                style={selectInputStyle}
                                                                allowClear
                                                            >
                                                                {AllDaysOfTheWeek.map((dayOfTheWeek, index) => {
                                                                    return (
                                                                        <Select.Option value={dayOfTheWeek} key={index}>
                                                                            {displayTextForDayOfTheWeek(dayOfTheWeek)}
                                                                        </Select.Option>
                                                                    );
                                                                })}
                                                            </Select>
                                                        </FormItem>
                                                        <FormItem
                                                            label="Release time"
                                                            name={[fieldData.name, 'releaseTime']}
                                                            style={compactFormItemStyle}
                                                            validateTrigger="onFinish"
                                                            rules={[
                                                                {
                                                                    validator: (_, value) => {
                                                                        const configuredReleaseDay = form.getFieldValue(
                                                                            [
                                                                                'cadenceTargetSets',
                                                                                i,
                                                                                'releaseDayOfWeek',
                                                                            ],
                                                                        );
                                                                        if (configuredReleaseDay && !value) {
                                                                            return Promise.reject(
                                                                                'Release time is required if release day of the week is selected',
                                                                            );
                                                                        } else if (i > 0) {
                                                                            const firstSetReleaseTime =
                                                                                form.getFieldValue([
                                                                                    'cadenceTargetSets',
                                                                                    0,
                                                                                    'releaseTime',
                                                                                ]);

                                                                            if (firstSetReleaseTime && !value) {
                                                                                return Promise.reject(
                                                                                    'Targets must be consistently configured across sets (release time configured in one or more preceding sets)',
                                                                                );
                                                                            } else if (!firstSetReleaseTime && value) {
                                                                                return Promise.reject(
                                                                                    'Targets must be consistently configured across sets (release time missing in one or more preceding sets)',
                                                                                );
                                                                            }
                                                                        }

                                                                        return Promise.resolve();
                                                                    },
                                                                },
                                                            ]}
                                                        >
                                                            {makeReleaseScheduleTimePicker(
                                                                timeInputStyle,
                                                                cadenceTypeVal === ReleaseScheduleCadenceStyle.Never,
                                                            )}
                                                        </FormItem>
                                                        {i === 0 &&
                                                            cadenceTypeVal !== ReleaseScheduleCadenceStyle.Never && (
                                                                <FormItem
                                                                    label={<span>&nbsp;</span>}
                                                                    colon={false}
                                                                    name="skipNextPossibleRelease"
                                                                    style={compactFormItemStyle}
                                                                    shouldUpdate
                                                                    validateTrigger="onFinish"
                                                                >
                                                                    <Space size={6}>
                                                                        <Checkbox
                                                                            checked={form.getFieldValue(
                                                                                'skipNextPossibleRelease',
                                                                            )}
                                                                            onChange={() => {
                                                                                const value =
                                                                                    form.getFieldValue(
                                                                                        'skipNextPossibleRelease',
                                                                                    );
                                                                                form.setFieldValue(
                                                                                    'skipNextPossibleRelease',
                                                                                    !value,
                                                                                );
                                                                            }}
                                                                        />
                                                                        <Text>Following week</Text>
                                                                        <Tooltip
                                                                            title={
                                                                                <div>
                                                                                    <p>
                                                                                        By default, Runway will schedule
                                                                                        release for the next available
                                                                                        instance of the selected target
                                                                                        day in the calendar (e.g. if
                                                                                        Wednesday is selected, Runway
                                                                                        will schedule for the first
                                                                                        possible Wednesday after
                                                                                        submission).
                                                                                    </p>
                                                                                    Enable this option to instead have
                                                                                    Runway schedule release for one week
                                                                                    later (e.g. if Wednesday is
                                                                                    selected, Runway will skip the first
                                                                                    Wednesday after submission and
                                                                                    instead schedule for the following
                                                                                    Wednesday).
                                                                                </div>
                                                                            }
                                                                        >
                                                                            <InfoCircleOutlined
                                                                                style={{ color: antGrey.grey7 }}
                                                                            />
                                                                        </Tooltip>
                                                                    </Space>
                                                                </FormItem>
                                                            )}
                                                    </Input.Group>
                                                </div>
                                            )}
                                            {cadenceTypeVal !== ReleaseScheduleCadenceStyle.Never && (
                                                <ReleaseScheduleCalendarPreview
                                                    set={(cadenceSetsVal as unknown as CadenceSetValue[])?.[i]}
                                                    applySkipSubmit={i === 0 && (skipNextPossibleSubmitVal ?? false)}
                                                    applySkipRelease={i === 0 && (skipNextPossibleReleaseVal ?? false)}
                                                    showSubmit={!isOTA}
                                                    showRelease={isAppleAppStore || isOTA}
                                                />
                                            )}
                                        </Card>
                                    );
                                })}
                            </>
                        );
                    }}
                </FormList>
                {cadenceTypeVal === ReleaseScheduleCadenceStyle.Weekly ?
                    <Button
                        onClick={handleAddSet}
                        disabled={cadenceSetsVal?.length >= 7}
                        type="default"
                        icon={<PlusOutlined />}
                        style={{ marginRight: 16, marginTop: 4 }}
                    >
                        Add another set of release event dates
                    </Button>
                :   null}
                <div style={{ paddingBottom: 8, paddingTop: 20 }}>
                    <Space>
                        <Text>Lifecycle freeze dates</Text>
                        <Tooltip
                            title={`Runway will pause scheduled automations related to kickoff, submission, and release during the selected lifecycle freeze dates.`}
                        >
                            <InfoCircleOutlined style={{ color: antGrey.grey7 }} />
                        </Tooltip>
                    </Space>
                </div>
                <FormList name="lifecycleFreezes">
                    {(fields, { remove }) => {
                        return (
                            <>
                                {fields.map((fieldData, i) => {
                                    return (
                                        <Card
                                            title={
                                                <div
                                                    style={{
                                                        display: 'flex',
                                                        flexDirection: 'row',
                                                        alignItems: 'center',
                                                        justifyContent: 'space-between',
                                                    }}
                                                >
                                                    <Text
                                                        style={{
                                                            width: 24,
                                                            height: 24,
                                                            borderRadius: 12,
                                                            backgroundColor: runwayColors.primary,
                                                            color: 'white',
                                                            display: 'flex',
                                                            justifyContent: 'center',
                                                            alignItems: 'center',
                                                            fontWeight: 'normal',
                                                            flexShrink: 0,
                                                        }}
                                                    >
                                                        {i + 1}
                                                    </Text>
                                                    <div>
                                                        <Button
                                                            icon={<CloseOutlined />}
                                                            type="text"
                                                            onClick={() => remove(i)}
                                                        />
                                                    </div>
                                                </div>
                                            }
                                            key={`&lifecycleFreezeSet=${i}`}
                                            style={{ marginBottom: '15px' }}
                                        >
                                            <Input.Group compact style={{ marginTop: 16 }}>
                                                <FormItem
                                                    name={[fieldData.name, 'lifecycleFreezeSetRange']}
                                                    shouldUpdate
                                                    validateTrigger="onFinish"
                                                    label="Freeze period"
                                                    style={compactFormItemStyle}
                                                    rules={[
                                                        {
                                                            validator: (_, value) => {
                                                                if (!value || value.length !== 2) {
                                                                    return Promise.reject(
                                                                        new Error('Please select a valid date range'),
                                                                    );
                                                                }

                                                                const [start, end] = value;

                                                                if (!dayjs.isDayjs(start) || !dayjs.isDayjs(end)) {
                                                                    return Promise.reject(
                                                                        new Error('Invalid date format'),
                                                                    );
                                                                }

                                                                return Promise.resolve();
                                                            },
                                                        },
                                                    ]}
                                                >
                                                    <DatePicker.RangePicker
                                                        disabledDate={(current) =>
                                                            current && current < dayjs().endOf('day').subtract(1, 'day')
                                                        }
                                                    />
                                                </FormItem>
                                                <FormItem
                                                    name={[fieldData.name, 'lifecycleFreezeAffectedEvents']}
                                                    shouldUpdate
                                                    validateTrigger="onFinish"
                                                    label="Events to freeze"
                                                    style={compactFormItemStyle}
                                                    rules={[
                                                        {
                                                            validator: (_, value) => {
                                                                if (!value || !value.length) {
                                                                    return Promise.reject(
                                                                        new Error(
                                                                            'At least one event must be selected',
                                                                        ),
                                                                    );
                                                                }

                                                                return Promise.resolve();
                                                            },
                                                        },
                                                    ]}
                                                >
                                                    <Select
                                                        mode="multiple"
                                                        options={lifecycleEventOptions}
                                                        placeholder="Select affected events"
                                                        style={{ width: 275 }}
                                                    />
                                                </FormItem>
                                            </Input.Group>
                                        </Card>
                                    );
                                })}
                            </>
                        );
                    }}
                </FormList>

                <Button onClick={handleAddLifecycleFreezeSet} type="default" icon={<PlusOutlined />}>
                    Add lifecycle freeze dates to prevent release events
                </Button>

                {cadenceTypeVal !== ReleaseScheduleCadenceStyle.Never && (
                    <div style={{ marginTop: 24 }}>
                        <Space size={6}>
                            <Checkbox
                                checked={updateExistingReleaseTargetDatesVal ?? false}
                                onChange={(e) =>
                                    form.setFieldValue('updateExistingReleaseTargetDates', e.target.checked)
                                }
                            >
                                Also update target dates on existing releases
                            </Checkbox>
                            <Tooltip title="When enabled, target dates on in-progress and upcoming releases will be recalculated to match the new schedule. Dates that have already passed are left unchanged.">
                                <InfoCircleOutlined style={{ color: antGrey.grey7 }} />
                            </Tooltip>
                        </Space>
                    </div>
                )}
            </ExtendedForm>

            <Footer style={{ ...styles.footerStyle }}>
                <PermissionedActionButton
                    actionType={RunwayUserAction.UAUpdateScheduleCadence}
                    onClick={() => form.submit()}
                    loading={updateCadenceIsLoading}
                    disabled={
                        !releaseScheduleFormState.isDirty &&
                        !isCadenceTimezoneDirty &&
                        !isSkipNextPossibleSubmitDirty &&
                        !isSkipNextPossibleReleaseDirty &&
                        !isUpdateExistingReleaseTargetDatesDirty
                    }
                    type="primary"
                >
                    Save changes
                </PermissionedActionButton>
            </Footer>
        </Layout>
    );
};

const ORDERED_DAYS = [
    DayOfTheWeek.Monday,
    DayOfTheWeek.Tuesday,
    DayOfTheWeek.Wednesday,
    DayOfTheWeek.Thursday,
    DayOfTheWeek.Friday,
    DayOfTheWeek.Saturday,
    DayOfTheWeek.Sunday,
];

const DAY_ABBREV: Record<DayOfTheWeek, string> = {
    [DayOfTheWeek.Monday]: 'Mon',
    [DayOfTheWeek.Tuesday]: 'Tue',
    [DayOfTheWeek.Wednesday]: 'Wed',
    [DayOfTheWeek.Thursday]: 'Thu',
    [DayOfTheWeek.Friday]: 'Fri',
    [DayOfTheWeek.Saturday]: 'Sat',
    [DayOfTheWeek.Sunday]: 'Sun',
};

type CadenceSetValue = {
    kickoffDayOfWeek: string | null;
    kickoffTime: dayjs.Dayjs | null;
    submitDayOfWeek: string | null;
    submitTime: dayjs.Dayjs | null;
    releaseDayOfWeek: string | null;
    releaseTime: dayjs.Dayjs | null;
};

const formatEventTime = (t: dayjs.Dayjs): string => (t.minute() === 0 ? t.format('ha') : t.format('h:mma'));

const dayIndex = (day: DayOfTheWeek): number => ORDERED_DAYS.indexOf(day);
const timeMinutes = (t: dayjs.Dayjs | null): number => (t ? t.hour() * 60 + t.minute() : 0);
const absOffset = (day: DayOfTheWeek, time: dayjs.Dayjs | null, week: number): number =>
    (week - 1) * 7 * 1440 + dayIndex(day) * 1440 + timeMinutes(time);

// Computes which week each event lands on using "first occurrence after preceding event" logic.
// If submit/release day+time falls before (or equal to) the preceding event within the same week,
// it automatically bumps to the next week. "Following week" adds an extra +1 on top.
const computeEventWeeks = (
    set: CadenceSetValue | undefined,
    applySkipSubmit: boolean,
    applySkipRelease: boolean,
    showSubmit: boolean,
    showRelease: boolean,
): { submitWeek: number; releaseWeek: number } => {
    const kickoffDay = set?.kickoffDayOfWeek as DayOfTheWeek | null;
    const kickoffTime = set?.kickoffTime ?? null;
    const submitDay = showSubmit ? (set?.submitDayOfWeek as DayOfTheWeek | null) : null;
    const submitTime = set?.submitTime ?? null;
    const releaseDay = showRelease ? (set?.releaseDayOfWeek as DayOfTheWeek | null) : null;
    const releaseTime = set?.releaseTime ?? null;

    // Submit: try week 1 first; bump to week 2 if it doesn't follow kickoff
    let submitWeekBase = 1;
    if (kickoffDay && submitDay) {
        const kickoffAbs = absOffset(kickoffDay, kickoffTime, 1);
        const submitAbs1 = absOffset(submitDay, submitTime, 1);
        submitWeekBase = submitAbs1 > kickoffAbs ? 1 : 2;
    }
    const submitWeek = submitWeekBase + (submitDay && applySkipSubmit ? 1 : 0);

    // Release: try same week as submit (or week 1 if no submit); bump if needed
    let releaseWeekBase = 1;
    if (releaseDay) {
        if (submitDay) {
            const submitAbs = absOffset(submitDay, submitTime, submitWeek);
            const releaseAbsAtSubmitWk = absOffset(releaseDay, releaseTime, submitWeek);
            releaseWeekBase = releaseAbsAtSubmitWk > submitAbs ? submitWeek : submitWeek + 1;
        } else if (kickoffDay) {
            const kickoffAbs = absOffset(kickoffDay, kickoffTime, 1);
            const releaseAbs1 = absOffset(releaseDay, releaseTime, 1);
            releaseWeekBase = releaseAbs1 > kickoffAbs ? 1 : 2;
        }
    }
    const releaseWeek = releaseWeekBase + (applySkipRelease ? 1 : 0);

    return { submitWeek, releaseWeek };
};

type CalEvent = { day: DayOfTheWeek; label: string; week: number };

const calPillStyle: React.CSSProperties = {
    backgroundColor: 'transparent',
    color: runwayColors.primary,
    border: `1.5px solid ${runwayColors.primary}`,
};

const ReleaseScheduleCalendarPreview: React.FC<{
    set: CadenceSetValue | undefined;
    applySkipSubmit: boolean;
    applySkipRelease: boolean;
    showSubmit: boolean;
    showRelease: boolean;
}> = ({ set, applySkipSubmit, applySkipRelease, showSubmit, showRelease }) => {
    if (!set) return null;

    const { submitWeek, releaseWeek } = computeEventWeeks(
        set,
        applySkipSubmit,
        applySkipRelease,
        showSubmit,
        showRelease,
    );

    const events: CalEvent[] = [];

    if (set.kickoffDayOfWeek) {
        events.push({
            day: set.kickoffDayOfWeek as DayOfTheWeek,
            label: `Kickoff${set.kickoffTime ? ' ' + formatEventTime(set.kickoffTime) : ''}`,
            week: 1,
        });
    }
    if (showSubmit && set.submitDayOfWeek) {
        events.push({
            day: set.submitDayOfWeek as DayOfTheWeek,
            label: `Submit${set.submitTime ? ' ' + formatEventTime(set.submitTime) : ''}`,
            week: submitWeek,
        });
    }
    if (showRelease && set.releaseDayOfWeek) {
        events.push({
            day: set.releaseDayOfWeek as DayOfTheWeek,
            label: `Release${set.releaseTime ? ' ' + formatEventTime(set.releaseTime) : ''}`,
            week: releaseWeek,
        });
    }

    if (events.length === 0) return null;

    const maxWeek = Math.max(...events.map((e) => e.week));

    const renderWeekRow = (weekNum: number) => (
        <div
            key={weekNum}
            style={{
                display: 'flex',
                borderBottom: weekNum < maxWeek ? `1px solid ${antGrey.grey4}` : undefined,
            }}
        >
            <div
                style={{
                    width: 56,
                    flexShrink: 0,
                    fontSize: 12,
                    color: antGrey.grey7,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    borderRight: `1px solid ${antGrey.grey4}`,
                    padding: '4px 2px',
                }}
            >
                Week {weekNum}
            </div>
            {ORDERED_DAYS.map((day) => {
                const dayEvents = events.filter((e) => e.day === day && e.week === weekNum);
                return (
                    <div
                        key={day}
                        style={{
                            width: 145,
                            flexShrink: 0,
                            borderRight: `1px solid ${antGrey.grey4}`,
                            padding: '4px 6px',
                            minHeight: 40,
                        }}
                    >
                        {dayEvents.map((e, idx) => (
                            <div
                                key={idx}
                                style={{
                                    fontSize: 13,
                                    borderRadius: 4,
                                    padding: '2px 6px',
                                    marginBottom: 2,
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap',
                                    fontWeight: 500,
                                    ...calPillStyle,
                                }}
                            >
                                {e.label}
                            </div>
                        ))}
                    </div>
                );
            })}
        </div>
    );

    return (
        <div
            style={{
                marginTop: 16,
                width: 'fit-content',
                border: `1px solid ${antGrey.grey4}`,
                borderRadius: 8,
                overflow: 'hidden',
            }}
        >
            <div
                style={{
                    display: 'flex',
                    backgroundColor: antGrey.grey3,
                    borderBottom: `1px solid ${antGrey.grey4}`,
                }}
            >
                <div style={{ width: 56, flexShrink: 0, borderRight: `1px solid ${antGrey.grey4}` }} />
                {ORDERED_DAYS.map((day) => (
                    <div
                        key={day}
                        style={{
                            width: 145,
                            flexShrink: 0,
                            textAlign: 'center',
                            fontWeight: 600,
                            fontSize: 12,
                            color: antGrey.grey8,
                            padding: '5px 0',
                            borderRight: `1px solid ${antGrey.grey4}`,
                        }}
                    >
                        {DAY_ABBREV[day]}
                    </div>
                ))}
            </div>
            {Array.from({ length: maxWeek }, (_, idx) => renderWeekRow(idx + 1))}
        </div>
    );
};

const makeReleaseScheduleTimePicker = (style: React.CSSProperties, disabled: boolean) => {
    return (
        <TimePicker
            defaultOpenValue={dayjs().set('hour', 0).set('minute', 0)}
            format="h:mm A"
            minuteStep={30}
            showNow={false}
            disabled={disabled}
            allowClear
            style={style}
        />
    );
};

export default ReleaseScheduleSettings;
